// The one place the front end talks to Express.
//
// Auth is a session cookie plus a CSRF token (docs/API.md §1.7). React never holds
// a credential — the session cookie is httpOnly and goes out automatically — so
// the only thing this wrapper has to attach is the CSRF token on mutations, and
// the only thing it has to parse is the standard error envelope.

import { createCsrfTokenState } from './csrf-token.js';

const CSRF_HEADER = 'X-CSRF-Token';
const CSRF_COOKIE = 'crm_csrf';

/**
 * The token also arrives as a readable cookie, which is the fallback if a mutation
 * somehow fires before /api/me resolves. Holding it in memory is preferred: it
 * survives nothing, which is the point.
 *
 * NO FALLBACK AFTER A SIGN-OUT. Once a held token is cleared (`setCsrfToken(null)`, which
 * the session provider does on sign-out), the cookie is not read again until a real token
 * is set: logout leaves `crm_csrf` in place and the next person's sign-in overwrites it
 * with theirs, so falling back would let this stale tab act as them. Without a token its
 * mutation is refused `403 CSRF_TOKEN_INVALID`, and the identity check below reloads it.
 * The rule is ./csrf-token.js's, tested in test/web/csrf-token.test.js.
 */
const csrf = createCsrfTokenState(() => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
});

/** Called by the session provider once GET /api/me returns, and with null on sign-out. */
export function setCsrfToken(token) {
  csrf.set(token ?? null);
}

function currentCsrfToken() {
  return csrf.current();
}

/** A non-2xx response, carrying the envelope's `code` so callers branch on that. */
export class ApiError extends Error {
  constructor({ status, code, message, details }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// One redirect per page load. Without the latch, a screen firing several requests
// in parallel would race a navigation each, and a 401 loop would be invisible.
let redirectingToLogin = false;

function goToLogin() {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  // No `?next=`. GET /auth/login ignores it and always lands a completed sign-in on
  // `/` (src/routes/auth.js, `AFTER_SIGN_IN`) — and the guard that used to make a
  // `next` value safe to honour, `safeNextPath`, was deleted when the parameter was
  // dropped server-side. Sending one anyway left the caller in the repository without
  // the guard: harmless while the value is discarded, and a loaded gun for whoever
  // implements deep-link-after-login by reading it. Restore the guard with the
  // feature, not before it.
  window.location.assign('/auth/login');
}

const SAFE_METHODS = new Set(['GET', 'HEAD']);

// WHAT A `403 CSRF_TOKEN_INVALID` MEANS, and why it is not simply thrown.
//
// Signing in again regenerates the session and its CSRF token (src/routes/auth.js). A tab
// left open across that holds the old token, so its next mutation is refused, and on a
// shared desk the new session may be a DIFFERENT person's. The session provider registers
// a hook here (`onCsrfInvalid`) that re-reads GET /api/me: the same user takes the fresh
// token (`setCsrfToken`) and answers `true`, and the request is sent again ONCE; a
// different user reloads the page and answers `false`, and the refusal is thrown
// (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "A different person
// signs in on a shared desk"). The decision is web/src/session/identity.js's.
let csrfInvalidHandler = null;
// The check in flight, so refusals that arrive together share one GET /api/me.
let csrfCheck = null;

/**
 * Registers the hook asked on `403 CSRF_TOKEN_INVALID`. It resolves `true` only when the
 * fresh token has been taken and the request may be sent again; anything else, a throw
 * included, throws the refusal. One hook at a time; returns its unregister, which removes
 * only this hook.
 */
export function onCsrfInvalid(handler) {
  csrfInvalidHandler = handler ?? null;
  return () => {
    if (csrfInvalidHandler === handler) csrfInvalidHandler = null;
  };
}

function recoverFromCsrfRefusal() {
  const handler = csrfInvalidHandler;
  if (!handler) return Promise.resolve(false);
  if (!csrfCheck) {
    csrfCheck = Promise.resolve()
      .then(() => handler())
      .then((retry) => retry === true, () => false)
      .finally(() => { csrfCheck = null; });
  }
  return csrfCheck;
}

/**
 * @param {string} path   Path under /api, e.g. `/tickets?status=open`.
 * @param {object} [options]
 * @param {string} [options.method]  Defaults to GET.
 * @param {object} [options.body]    Serialised as JSON.
 * @param {AbortSignal} [options.signal]
 */
export function apiFetch(path, options) {
  return request(path, options, false);
}

async function request(path, { method = 'GET', body, signal } = {}, retried) {
  const headers = {};
  const init = {
    method,
    // Explicit rather than relying on the default: the session cookie is the whole
    // credential, and a default that changes silently would sign everyone out.
    credentials: 'same-origin',
    headers,
    signal,
  };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  if (!SAFE_METHODS.has(method)) {
    const token = currentCsrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }

  const response = await fetch(`/api${path}`, init);

  if (response.status === 204) return null;

  // STATUS BEFORE CONTENT TYPE, deliberately, and this order is the whole reason a
  // 401 reliably becomes a sign-in.
  //
  // This check used to live below the content-type branch, and that branch returns:
  // a 401 answered as anything other than JSON threw "The server returned 401 without
  // JSON." and NEVER reached `goToLogin()`. It worked only because the real app
  // answers 401 with `application/json` — proven, and load-bearing enough that the
  // server side of it is asserted on the real app in test/auth/sign-in-e2e.test.js
  // ('an anonymous /api/me is 401 in the JSON envelope…'). But "the redirect works
  // because of a header we happen to set elsewhere" is one proxy, one error page, one
  // App Service 401 away from a support desk that can no longer sign in. The status is
  // the fact; the body is only how the fact is phrased.
  if (response.status === 401) goToLogin();

  // The API answers JSON for everything including errors (docs/API.md §1.1). HTML
  // here means something upstream of Express answered — a proxy, or a build that
  // fell through to index.html — and saying so beats "Unexpected token <".
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (response.ok) return null;
    throw new ApiError({
      status: response.status,
      code: 'INTERNAL_ERROR',
      message: `The server returned ${response.status} without JSON.`,
    });
  }

  const payload = await response.json();

  if (response.ok) return payload;

  const envelope = payload?.error ?? {};
  const error = new ApiError({
    status: response.status,
    code: envelope.code ?? 'INTERNAL_ERROR',
    message: envelope.message ?? 'Something went wrong.',
    details: envelope.details,
  });

  // Once per request: a second refusal after the retry is thrown, never looped on. Only a
  // mutation carries the token, so only a mutation can be refused for it.
  if (
    error.status === 403 && error.code === 'CSRF_TOKEN_INVALID'
    && !retried && !SAFE_METHODS.has(method)
    && await recoverFromCsrfRefusal()
  ) {
    return request(path, { method, body, signal }, true);
  }
  throw error;
}

/**
 * Ends the session on this computer. `POST /auth/logout`, with the CSRF token the
 * route requires (src/routes/auth.js) — the same token GET /api/me handed us.
 *
 * NOT `api.post`: everything else in this file is under `/api`, and this route is not.
 *
 * WHAT THIS DOES AND DOES NOT END, because the difference decides who can use this
 * machine next:
 *
 *   ENDS — the session row in `user_sessions` and the `crm_session` cookie. That is
 *     the credential this app checks on every request, and the 12-hour rolling window
 *     a shared support desk otherwise leaves running for whoever sits down next. The
 *     server destroys it before it redirects, on purpose ("ours first", src/routes/
 *     auth.js), so it is gone even if nothing below happens.
 *   DOES NOT END — the identity provider's own SSO session. The route answers 302 to
 *     Entra's `end_session_endpoint`, and a `fetch` cannot deliver the browser there:
 *     `redirect: 'manual'` makes the response opaque, so the Location is unreadable
 *     from script, and `redirect: 'follow'` would send a cross-origin request the CORS
 *     check discards. Ending it needs a TOP-LEVEL navigation, which needs the URL in
 *     something script can read. Recorded in
 *     docs/superpowers/plans/2026-08-20-entra-sso-followups.md §5.
 *
 * `redirect: 'manual'` is therefore also how success is recognised: an opaque redirect
 * (`status === 0`) means the route ran and destroyed the session, while anything with
 * a readable status is a refusal — a missing or stale CSRF token is 403 — and must be
 * reported rather than silently treated as a sign-out that happened.
 */
export async function signOut() {
  const token = currentCsrfToken();
  const response = await fetch('/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
    headers: token ? { [CSRF_HEADER]: token } : {},
    redirect: 'manual',
  });

  if (response.type === 'opaqueredirect' || response.status === 0) return;

  let envelope = {};
  try {
    envelope = (await response.json())?.error ?? {};
  } catch {
    // A refusal that is not JSON is still a refusal; fall through to the envelope
    // defaults rather than replacing "sign-out failed" with "bad JSON".
  }
  throw new ApiError({
    status: response.status,
    code: envelope.code ?? 'INTERNAL_ERROR',
    message: envelope.message ?? `Sign-out was refused (${response.status}).`,
    details: envelope.details,
  });
}

export const api = {
  get: (path, options) => apiFetch(path, { ...options, method: 'GET' }),
  post: (path, body, options) => apiFetch(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => apiFetch(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => apiFetch(path, { ...options, method: 'PUT', body }),
  del: (path, options) => apiFetch(path, { ...options, method: 'DELETE' }),
};
